import * as d3 from 'd3';
import moment from 'moment';
import { Measurements } from '~/utility/BinSentry-ui-utility';
import '../../common/bn-icon';
import './bin-levels';
import { LitEntity, SirenActionMixin, html, css, svg, renderIf } from '../../utility/lit-entity';
import { LevelColoursMixin } from '../../utility/level-colours-mixin';
import { sharedStyles } from '../../styles/shared-styles';
import '../../common/bn-loading';

const DAYS_PER_WEEK = 7;
const READINGS_PER_DAY = 6;
const FONT_SIZE = 12;
const FONT_OFFSET = 12;
const PADDING = 3;
const HEADING_HEIGHT = FONT_SIZE + 2 * PADDING;
const LEVELS_OFFSET = HEADING_HEIGHT + 2 * PADDING;

export default class BinCalendar extends LevelColoursMixin(SirenActionMixin(LitEntity)) {
  constructor() {
    super();
    this.end = moment().startOf('week').add(1, 'week').utc().toISOString();
    this.margin = { top: 0, right: 0, bottom: 0, left: 0 };
    this.nextButtonLabel = 'Next';
    this.prevButtonLabel = 'Prev';
    this.readingsPerDay = READINGS_PER_DAY;
    this.levelPaddingPx = PADDING;
    this.displayedWeeks = 1;
  }

  static get properties() {
    return {
      points: { type: Array, attribute: false },
      hoverPoint: { type: Object, attribute: false },
      selectedDate: { type: String, attribute: false },
      selectedPoint: { type: Object, attribute: false },
      nextPoint: { type: Object, attribute: false },
      prevPoint: { type: Object, attribute: false },
      hide: { type: Boolean, attribute: false },
      start: { type: String },
      end: { type: String },
      highlight: { type: String },
      readingsPerDay: { type: Number, attribute: false },
      hideForecast: { type: Boolean, attribute: 'hide-forecast' },
      showNextPreviousNav: { type: Boolean, attribute: 'show-next-previous-nav' },
      nextButtonLabel: { type: String, attribute: 'next-button-label' },
      prevButtonLabel: { type: String, attribute: 'prev-button-label' },
      levelPaddingPx: { type: Number, attribute: 'level-padding-px' },
      displayedWeeks: { type: Number, attribute: 'displayed-weeks' },
    };
  }

  set highlight(value) {
    const date = moment(value);
    if (date.toISOString() === this.selectedDate) {
      return;
    }
    this.selectedDate = date.toISOString();
    this._highlightSelectedDate();
  }

  static get styles() {
    return [sharedStyles, css`
      .flex-container {
        display: flex;
        align-content: stretch;
        min-height: 100px;
      }
      .page {
        display: flex;
        align-items: center;
        cursor: pointer;
        font-weight: bold;
        font-size: 1.5em;
        padding: 10px;
        color: var(--blue-50);
      }
      .page:hover {
        background-color: var(--hover-color);
        color: var(--blue-60);
      }
      .calendar {
        flex-grow: 1;
      }
      svg {
        height: 100%;
        width: 100%;
        display: block;
        cursor: pointer;
      }
      .day {
        fill: var(--surface);
        stroke: var(--blue-40);
        pointer-events: all;
      }
      text {
        fill: var(--black-base);
        font-size: 12px;
      }
      .dayOfWeek rect {
        stroke: var(--blue-40);
        fill: var(--blue-40);
      }
      .dayOfWeek.today rect {
        fill: var(--blue-50);
      }
      .day:hover .dayOfWeek rect {
        fill: var(--blue-50);
      }
      .reading .time {
        stroke: transparent;
        fill: transparent;
        opacity: 0;
      }
      .reading.hover .time,
      .reading.selected .time {
        stroke: var(--blue-50);
        fill: var(--blue-40);
        opacity: 1;
      }
      .reading .level {
        fill: var(--green-15);
        opacity: 1;
      }
      .reading.forecast .level {
        fill: var(--blue-60);
      }
      .reading.hover .level,
      .reading.selected .level {
        opacity: 1;
      }
      .time {
        display: flex;
        padding: 3px;
        border: 1px solid red;
        justify-content: center;
      }
      .time select {
        width: auto;
        text-align: right;
      }
      .time .divider {
        margin: 0 .5em;
      }
      .next-previous-nav {
        margin-left: 3em;
        display: flex;
      }
      .next-previous-nav button {
        margin-right: 1em;
      }
    `];
  }

  render() {
    if (!this.start) {
      return html`<bn-loading></bn-loading>`;
    }
    return html`
      <div class="flex-container">
        <div class="page" @click="${this._prevWeek}">
          <bn-icon name="far fa-chevron-circle-left"></bn-icon>
        </div>
        <div class="calendar">
          ${this._renderSvg()}
          <bin-levels
            .href="${this.levelsHref}"
            .start="${this.start}"
            .end="${this.end}"
            @update="${this._onUpdateLevels}"
          ></bin-levels>
          ${renderIf(!this.hideForecast, html`
            <lit-entity .href="${this.statsHref}"  @update="${this._onUpdateStats}"></lit-entity>
          `)}
        </div>
        <div class="page" @click="${this._nextWeek}">
          <bn-icon name="far fa-chevron-circle-right"></bn-icon>
        </div>
      </div>
      ${renderIf(this.showNextPreviousNav, () => html`
        <div class="next-previous-nav">
          <button ?disabled=${!this.prevPoint} class="button button--plain" href="#" @click="${this._clickPrev}">${this.prevButtonLabel}</button>
          <button ?disabled=${!this.nextPoint} class="button button--plain" href="#" @click="${this._clickNext}">${this.nextButtonLabel}</button>
        </div>`)}
    `;
  }

  _renderSvg() {
    if (this.hide) {
      return html``;
    }
    const calendar = this.shadowRoot.querySelector('.calendar');
    if (!calendar) {
      return html``;
    }
    const w = calendar.clientWidth;
    const h = calendar.clientHeight;
    this.width = w - this.margin.left - this.margin.right;
    this.height = h - this.margin.top - this.margin.bottom;
    if (w < 10 || h < LEVELS_OFFSET + 10) {
      return html``;
    }
    return svg`
      <svg
        viewBox="0 0 ${w} ${h}"
        width="100%"
        height="100%"
        @click="${this._onClick}"
        @mousemove="${this._onMouseMove}"
        @mouseout="${() => this.hoverPoint = null}">
        <g transform="translate(${this.margin.left}, ${this.margin.top})">
          <g class="week">
            ${this._renderDays()}
          </g>
          <g class="levels" transform="translate(0, ${LEVELS_OFFSET})">
            ${this._renderLevels()}
          </g>
        </g>
      </svg>
    `;
  }

  _renderDays() {
    const dayCount = this.displayedWeeks * DAYS_PER_WEEK;
    const dayWidth = this.width / dayCount;
    const day = moment(this.start);
    const days = [];
    for (let i = 0; i < dayCount; ++i) {
      days.push(moment(day));
      day.add(1, 'day');
    }
    return days.map((day, i) => {
      const isToday = moment().isSame(day, 'day');
      const labelWidth = dayWidth - 2 * PADDING;

      const showMonth = i === 0 || day.date() === 1 || i === dayCount - 1;
      const dateText = showMonth ? day.format('MMM D, ddd') : day.format('D ddd');

      return svg`
        <g transform="translate(${i * dayWidth}, 0)">
          <rect class="day" x="0" y="0" height="${this.height}" width="${dayWidth}"></rect>
          <g class="dayOfWeek ${isToday ? 'today' : ''}" transform="translate(${PADDING}, ${PADDING})">
            <rect x="0" y="0" height="${HEADING_HEIGHT}" width="${labelWidth}"></rect>
            <text x="${labelWidth / 2}" y="${PADDING + FONT_OFFSET}" text-anchor="middle">
              ${dateText}
            </text>
          </g>
        </g>
      `;
    });
  }

  _renderLevels() {
    if (!this.points) {
      return svg``;
    }

    const dayCount = this.displayedWeeks * DAYS_PER_WEEK;
    const barWidth = Math.max(3, (this.width / dayCount / this.readingsPerDay) - 2 * this.levelPaddingPx);
    const height = this.height - (LEVELS_OFFSET + PADDING);
    const measurement = Measurements.get('binLevel');
    const { bulkDensity, totalVolume } = this;
    const top = measurement.convert({ volume: this.totalVolume, bulkDensity, totalVolume });

    this.xScale = d3.scaleTime()
      .range([0, this.width])
      .domain([moment(this.start), moment(this.end)]);

    this.yScale = d3.scaleLinear()
      .range([1, height])
      .domain([0, Math.ceil(Math.max(...this.points.map((p) => p.value), top, 1))]);

    return this.points.map((point) => this._renderPoint({ point, height, barWidth }));
  }

  _renderPoint({ point, height, barWidth }) {
    const x = this.xScale(point.date);
    const y = this.yScale(point.value);
    const classes = ['reading'];
    if (point.forecast) {
      classes.push('forecast');
    }
    if (this.selectedPoint === point) {
      classes.push('selected');
    }
    if (this.hoverPoint === point) {
      classes.push('hover');
    }
    return svg`
        <g class="${classes.join(' ')}">
          <rect
            x="${x - (barWidth / 2) - PADDING}"
            y="${-PADDING}"
            height="${height + PADDING * 2}"
            width="${barWidth + PADDING * 2}"
            class="time"
          ></rect>
          <rect
            x="${x - (barWidth / 2)}"
            y="${height - y}"
            height="${y}"
            width="${barWidth}"
            class="level"
            style="fill: ${this._getColour(point)}"
          ></rect>
        </g>
      `;
  }

  firstUpdated() {
    if (!this.start && this.end) {
      this.start = moment(this.end).subtract(this.displayedWeeks, 'week').utc().toISOString();
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this._onResize = this._onResize.bind(this);
    window.addEventListener('resize', this._onResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this._onResize);
  }

  _onResize() {
    this.hide = true;
    this.resizeHandle && clearTimeout(this.resizeHandle);
    this.resizeHandle = setTimeout(() => {
      this.svg = undefined;
      this.svgPoint = undefined;
      this.hide = false;
    }, 200);
  }

  _onMouseMove(e) {
    this.hoverPoint = this._getClosestPointToCursor(e)?.point;
  }

  _onClick(e) {
    this.__trace('_onClick');
    this._selectPoint(this._getClosestPointToCursor(e));
  }

  _entityChanged(entity) {
    super._entityChanged(entity);
    this.levelsHref = this.getHref('https://api.binsentry.com/rel/bin-levels');
    this.statsHref = this.getHref('https://api.binsentry.com/rel/bin-stat-latest');
    this.totalVolume = this.getProperty('volumes.fullTotal');
    this.bulkDensity = this.getProperty('bulkDensity');
  }

  async _onUpdateLevels(e) {
    const { entity } = e.detail;
    const action = entity.getActionByName('custom-date-range');
    const fields = this.getActionFields(action);
    if ((!this.start && !this.end) || (fields.get('startDate') === this.start && fields.get('endDate') === this.end)) {
      this.levelsEntity = entity;
      this._updateGraph();
      return;
    }
    this.start && fields.set('startDate', this.start);
    this.end && fields.set('endDate', this.end);
    const { entity: newEntity } = await this.submitAction(action, fields);
    this.levelsEntity = newEntity;
    this._updateGraph();
  }

  _onUpdateStats(e) {
    this.statsEntity = e.detail.entity;
    this._updateGraph();
  }

  _prevWeek() {
    this.start = moment(this.start).subtract(1, 'week').utc().toISOString();
    this.end = moment(this.end).subtract(1, 'week').utc().toISOString();
  }

  _nextWeek() {
    this.start = moment(this.start).add(1, 'week').utc().toISOString();
    this.end = moment(this.end).add(1, 'week').utc().toISOString();
  }

  _selectPoint({ point, next, prev }) {
    this.__trace('_selectPoint: ', point);
    this.selectedPoint = point;
    this.nextPoint = next;
    this.prevPoint = prev;
    if (point) {
      this.selectedDate = moment(point.date).toISOString();
      this.dispatchEvent(new CustomEvent('select', { detail: { point, next, prev } } ));
    }
  }

  _highlightSelectedDate() {
    this.__trace('_highlightSelectedDate: ', this.selectedDate);
    if (!this.selectedDate) {
      return;
    }
    const date = moment(this.selectedDate);
    if (this.points && date.isBetween(this.start, this.end)) {
      this._selectPoint(this._getClosestPointToDate(date));
    } else {
      this.selectedPoint = undefined;
    }
  }

  _getClosestPointToDate(date) {
    if (!this.points) {
      return false;
    }
    return this.points.reduce((accumulator, curr) => {
      if (!accumulator.point || Math.abs(curr.date.diff(date)) < Math.abs(accumulator.point.date.diff(date))) {
        return { point: curr, prev: accumulator.point };
      }
      if (!accumulator.next) {
        return { ...accumulator, next: curr };
      }
      return accumulator;
    }, {});
  }

  _getClosestPointToCursor(e) {
    this.svg = this.svg || this.shadowRoot.querySelector('svg');
    this.svgPoint = this.svgPoint || this.svg.createSVGPoint();
    this.svgPoint.x = e.clientX;
    this.svgPoint.y = e.clientY;
    const pos = this.svgPoint.matrixTransform(this.svg.getScreenCTM().inverse());
    const date = this.xScale && moment(this.xScale.invert(pos.x));
    return this._getClosestPointToDate(date);
  }

  _getColour(p) {
    if (p.forecast) {
      return 'var(--blue-60)';
    }
    const top = this.yScale.domain()[1];
    const percent = p.value / top * 100;
    return this.getLevelColour(percent);
  }

  _getHistory() {
    const levels = this.levelsEntity && this.levelsEntity.entities;
    if (!levels) {
      return [];
    }
    const points = [];
    const measurement = Measurements.get('binLevel');
    const { bulkDensity, totalVolume } = this;
    levels.forEach((level) => {
      const { publishedAt, volume } = level.properties;
      const id = level.getLinkByRel('self').href;
      const value = measurement.convert({ volume, bulkDensity, totalVolume });
      points.push({ id, ...level.properties, date: moment(publishedAt), value, levelEntity: level });
    });
    points.sort((a, b) => a.date.diff(b.date));
    return points;
  }

  _calculateReadingsPerDay(historyPoints) {
    if (!historyPoints.length) {
      return READINGS_PER_DAY;
    }
    const oneDayBeforeLastReading = historyPoints[historyPoints.length - 1].date.clone().subtract(2, 'day');
    const realReadingsPerDay = historyPoints.filter(point => oneDayBeforeLastReading.isBefore(point.date)).length / 2;
    return realReadingsPerDay > 15 ? 24 : READINGS_PER_DAY;
  }

  _getForecast() {
    if (!this.statsEntity) {
      return [];
    }
    const points = [];
    const { forecast } = this.statsEntity.properties.volume;
    if (!forecast) {
      return [];
    }
    const measurement = Measurements.get('binLevel');
    const { bulkDensity, totalVolume } = this;
    forecast.forEach((f) => {
      const date = moment(f.publishedAt);
      if (date.isAfter(this.end)) {
        return;
      }
      const volume = f.low;
      const value = measurement.convert({ volume, bulkDensity, totalVolume });
      points.push({ date, value, forecast: true, volume });
    });
    points.sort((a, b) => a.date.diff(b.date));
    return points;
  }

  _padPoints(points) {
    let date = points.length ? moment(points[0].date).subtract(4, 'hours') : moment();
    while (date.isAfter(this.start)) {
      points.unshift({ date, value: 0, forecast: true, volume: 0 });
      date = moment(date).subtract(4, 'hours');
    }
    date = points.length ? moment(points[points.length - 1].date).add(4, 'hours') : moment();
    while (date.isBefore(this.end)) {
      points.push({ date, value: 0, forecast: true, volume: 0 });
      date = moment(date).add(4, 'hours');
    }
  }

  _updateGraph() {
    this.__trace('_updateGraph');
    const history = this._getHistory();
    let forecast = this._getForecast();
    this.readingsPerDay = this._calculateReadingsPerDay(history);
    // Filter out all forecast readings which overlap with or come before the most recent regular reading
    const lastHistory = history.length > 0 && history[history.length - 1];
    if (lastHistory && forecast.length) {
      forecast = forecast.filter((p) => p.date - lastHistory.date >= (60 * 60 * 1000));
    }
    this.points = [...history, ...forecast];
    this._padPoints(this.points);
    this._highlightSelectedDate();
  }

  _clickNext() {
    if (this.nextPoint) {
      this._selectPoint(this._getClosestPointToDate(this.nextPoint.date));
    }
  }

  _clickPrev() {
    if (this.prevPoint) {
      this._selectPoint(this._getClosestPointToDate(this.prevPoint.date));
    }
  }
}

window.customElements.define('bin-calendar', BinCalendar);
